import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'memoize',
  standalone: false,
  pure: true
})
export class MemoizePipe implements PipeTransform {
  /**
   * Why this pipe exists:
   * Angular Material `mat-select-trigger` fully controls the closed-state display text.
   * If we do not resolve the selected label explicitly, the trigger can show blank text
   * in edit/default-value scenarios (especially after Angular Material legacy -> MDC migration).
   *
   * What this pipe does:
   * Maps the current form control value(s) to display label(s) from `options`.
   * - Supports single select: returns one matched option label.
   * - Supports multi select: returns comma-separated matched labels.
   * - Normalizes comparisons using String(...) to handle id type mismatch (e.g. `1` vs `"1"`).
   *
   * Why pipe (instead of component method call from template):
   * Keeps template logic declarative, reusable, and easier to maintain.
   */
  transform(
    options: Array<Record<string, unknown>> | null | undefined,
    selectedValue: unknown,
    idKey = 'id',
    valueKey = 'value'
  ): string {
    if (!options?.length || this.isEmptyValue(selectedValue)) {
      return '';
    }

    if (Array.isArray(selectedValue)) {
      const selectedValues = new Set(selectedValue.map(value => String(value)));
      const selectedOptions = options.filter(option => {
        const optionId = option?.[idKey];
        return optionId !== null && optionId !== undefined && selectedValues.has(String(optionId));
      });
      return selectedOptions
        .map(option => option?.[valueKey])
        .filter(value => value !== null && value !== undefined && value !== '')
        .map(value => String(value))
        .join(', ');
    }

    const matchedOption = options.find(option => {
      const optionId = option?.[idKey];
      return optionId !== null && optionId !== undefined && String(optionId) === String(selectedValue);
    });

    const displayValue = matchedOption?.[valueKey];
    return displayValue === null || displayValue === undefined ? '' : String(displayValue);
  }

  private isEmptyValue(value: unknown): boolean {
    return value === null || value === undefined || value === '';
  }
}